import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useVersionStore } from '@/stores/versionStore';
import { usePublishVersion } from '@/hooks/useVersion';
import { PublishVersionInput, publishVersionSchema } from '../types';
import { toast } from 'sonner';

export function VersionPublish() {
  const { selectedVersion, isPublishModalOpen, closePublishModal } = useVersionStore();
  const publishVersion = usePublishVersion();

  const form = useForm<PublishVersionInput>({
    resolver: zodResolver(publishVersionSchema),
    defaultValues: {
      description: selectedVersion?.description || '',
      effective_date: selectedVersion?.effective_date?.split('T')[0] || ''
    }
  });

  const onSubmit = async (data: PublishVersionInput) => {
    if (!selectedVersion) return;

    const effectiveDate = new Date(data.effective_date);
    effectiveDate.setUTCHours(0, 0, 0, 0);

    try {
      await publishVersion.mutateAsync({
        id: selectedVersion.id,
        data: {
          ...data,
          effective_date: effectiveDate.toISOString()
        }
      });
      
      closePublishModal();
      form.reset();
      
      toast.success(`Version ${selectedVersion.version_number} published successfully`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to publish version");
    }
  };

  if (!selectedVersion) return null;

  return (
    <Dialog open={isPublishModalOpen} onOpenChange={closePublishModal}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Publish Version {selectedVersion.version_number}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="effective_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Effective Date</FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      {...field}
                      required
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Version description" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={closePublishModal}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={publishVersion.isPending}
              >
                {publishVersion.isPending ? 'Publishing...' : 'Publish Version'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
